// ============================================
// Date Range Store (Zustand)
// ============================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DateRange } from '../types/api';

// Preset date ranges
export type DatePreset = 'today' | 'yesterday' | 'last_7_days' | 'last_30_days' | 'this_month' | 'last_month' | 'this_quarter' | 'custom';

interface DateRangeState {
  dateRange: DateRange;
  preset: DatePreset;
  compareEnabled: boolean;
  compareDateRange: DateRange | null;

  // Actions
  setDateRange: (range: DateRange) => void;
  setPreset: (preset: DatePreset) => void;
  toggleCompare: () => void;
  setCompareDateRange: (range: DateRange | null) => void;
}

// Format Date to YYYY-MM-DD using local timezone (avoids UTC shift from toISOString)
function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Get date range for a preset
 */
function getPresetDateRange(preset: DatePreset): DateRange {
  const today = new Date();

  switch (preset) {
    case 'today':
      return { start: formatLocalDate(today), end: formatLocalDate(today) };

    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { start: formatLocalDate(yesterday), end: formatLocalDate(yesterday) };
    }

    case 'last_7_days': {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return { start: formatLocalDate(start), end: formatLocalDate(today) };
    }

    case 'last_30_days': {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return { start: formatLocalDate(start), end: formatLocalDate(today) };
    }

    case 'this_month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: formatLocalDate(start), end: formatLocalDate(today) };
    }

    case 'last_month': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: formatLocalDate(start), end: formatLocalDate(end) };
    }

    case 'this_quarter': {
      const quarterStart = Math.floor(today.getMonth() / 3) * 3;
      const start = new Date(today.getFullYear(), quarterStart, 1);
      return { start: formatLocalDate(start), end: formatLocalDate(today) };
    }

    default:
      // Default to last 30 days
      const defaultStart = new Date(today);
      defaultStart.setDate(defaultStart.getDate() - 29);
      return { start: formatLocalDate(defaultStart), end: formatLocalDate(today) };
  }
}

/**
 * Get comparison date range (previous period)
 */
function getPreviousPeriod(dateRange: DateRange): DateRange {
  const startDate = new Date(dateRange.start);
  const endDate = new Date(dateRange.end);
  const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  const prevEnd = new Date(startDate);
  prevEnd.setDate(prevEnd.getDate() - 1);

  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - daysDiff + 1);

  return {
    start: formatLocalDate(prevStart),
    end: formatLocalDate(prevEnd),
  };
}

// Create store
export const useDateRangeStore = create<DateRangeState>()(
  persist(
    (set, get) => ({
      dateRange: getPresetDateRange('this_month'),
      preset: 'this_month',
      compareEnabled: false,
      compareDateRange: null,

      setDateRange: (range) => {
        set({
          dateRange: range,
          preset: 'custom',
          compareDateRange: get().compareEnabled ? getPreviousPeriod(range) : null,
        });
      },

      setPreset: (preset) => {
        const dateRange = getPresetDateRange(preset);
        set({
          dateRange,
          preset,
          compareDateRange: get().compareEnabled ? getPreviousPeriod(dateRange) : null,
        });
      },

      toggleCompare: () => {
        const { compareEnabled, dateRange } = get();
        set({
          compareEnabled: !compareEnabled,
          compareDateRange: !compareEnabled ? getPreviousPeriod(dateRange) : null,
        });
      },

      setCompareDateRange: (range) => {
        set({ compareDateRange: range });
      },
    }),
    {
      name: 'spacet-date-range',
      partialize: (state) => ({
        // Don't persist preset - always start fresh with this_month
        compareEnabled: state.compareEnabled,
      }),
    }
  )
);

// Helper hooks
export function useDateRange() {
  const { dateRange, setDateRange, preset, setPreset } = useDateRangeStore();
  return { dateRange, setDateRange, preset, setPreset };
}

export function useCompare() {
  const { compareEnabled, compareDateRange, toggleCompare, setCompareDateRange } = useDateRangeStore();
  return { compareEnabled, compareDateRange, toggleCompare, setCompareDateRange };
}

// Export preset options for UI
export const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'today', label: 'Hôm nay' },
  { value: 'yesterday', label: 'Hôm qua' },
  { value: 'last_7_days', label: '7 ngày qua' },
  { value: 'last_30_days', label: '30 ngày qua' },
  { value: 'this_month', label: 'Tháng này' },
  { value: 'last_month', label: 'Tháng trước' },
  { value: 'this_quarter', label: 'Quý này' },
  { value: 'custom', label: 'Tùy chọn' },
];
